import { formatMetric, safeNumber, type AnalysisData } from './analysis.shared';
import {
  getMealsArrayFromNutritionalAnalysis,
  type NutritionalAnalysisMicrosView,
} from './mergeMealPlanNutritionalAnalysis';
import type { NutritionReportRow } from './NutritionReportSectionTable';

export type FoodContributionItem = { name: string; amount: number; hasContribution: boolean };

export type FoodContributionResult = {
  nutrientLabel: string;
  unit: string;
  items: FoodContributionItem[];
  targetLine: string | null;
};

function num(obj: Record<string, unknown> | null | undefined, keys: string[]): number | null {
  if (!obj) return null;
  for (const k of keys) {
    const n = safeNumber(obj[k], Number.NaN);
    if (Number.isFinite(n)) return n;
  }
  return null;
}

function readFromMeal(r: Record<string, unknown>, gramKeys: string[], nutritionKeys: string[] = gramKeys): number {
  const macros = r.macros && typeof r.macros === 'object' && !Array.isArray(r.macros) ? (r.macros as Record<string, unknown>) : null;
  const grams =
    macros && typeof macros.grams === 'object' && macros.grams && !Array.isArray(macros.grams)
      ? (macros.grams as Record<string, unknown>)
      : null;
  const nut =
    r.nutrition && typeof r.nutrition === 'object' && !Array.isArray(r.nutrition) ? (r.nutrition as Record<string, unknown>) : null;
  const a = num(grams, gramKeys) ?? num(nut, nutritionKeys) ?? num(r, gramKeys);
  return a != null && Number.isFinite(a) ? Math.max(0, a) : 0;
}

function mealLabel(r: Record<string, unknown>): string {
  return String(r.mealName ?? r.name ?? r.label ?? r.slotName ?? 'Meal').trim() || 'Meal';
}

type RowCtx = { sectionId: string; sectionTitle: string; row: NutritionReportRow };

function totalForRow(
  data: AnalysisData,
  { sectionId, sectionTitle, row }: RowCtx,
): { total: number; unit: string } | null {
  const id = row.id;
  if (sectionId === 'energy' || id === 'energy-row') return { total: data.energy, unit: 'kcal' };
  if (id === 'carbs') return { total: data.carbs, unit: 'g' };
  if (id === 'protein') return { total: data.protein, unit: 'g' };
  if (id === 'fat') return { total: data.fat, unit: 'g' };
  if (id === 'water') return { total: data.water, unit: 'L' };
  if (id === 'alcohol') {
    const k = data.kpis.find((k) => k.label.trim().toLowerCase() === 'alcohol');
    return { total: k?.value ?? 0, unit: 'g' };
  }
  if (id === 'starch') {
    const g = data.mealPlanNutritionReportDetail?.starch;
    return { total: g != null && Number.isFinite(g) ? g : 0, unit: 'g' };
  }
  if (id === 'oligosaccharide') {
    const g = data.mealPlanNutritionReportDetail?.oligosaccharide;
    return { total: g != null && Number.isFinite(g) ? g : 0, unit: 'g' };
  }
  if (id === 'nsp-fibre') return { total: data.fibre, unit: 'g' };
  if (id === 'sugars') {
    const g = data.mealPlanNutritionReportDetail?.sugar;
    return { total: g != null && Number.isFinite(g) ? g : 0, unit: 'g' };
  }
  if (id === 'free-sugars') {
    const g = data.mealPlanNutritionReportDetail?.free_sugars;
    return { total: g != null && Number.isFinite(g) ? g : data.freeSugar, unit: 'g' };
  }
  if (id === 'sat-fat') return { total: data.saturatedFat, unit: 'g' };
  if (id === 'mono-fat') {
    const k = data.kpis.find((x) => x.label.trim().toLowerCase() === 'monounsaturated fat');
    const g = data.fatSubtypeGrams?.monounsaturated;
    const fromKpi = k != null && Number.isFinite(k.value) ? k.value : null;
    const fromG = typeof g === 'number' && Number.isFinite(g) ? g : null;
    return { total: fromKpi ?? fromG ?? 0, unit: 'g' };
  }
  if (id === 'poly-fat') {
    const k = data.kpis.find((x) => x.label.trim().toLowerCase() === 'polyunsaturated fat');
    const g = data.fatSubtypeGrams?.polyunsaturated;
    const fromKpi = k != null && Number.isFinite(k.value) ? k.value : null;
    const fromG = typeof g === 'number' && Number.isFinite(g) ? g : null;
    return { total: fromKpi ?? fromG ?? 0, unit: 'g' };
  }
  if (id === 'cholesterol') {
    const k = data.kpis.find((x) => x.label.trim().toLowerCase() === 'cholesterol');
    return { total: k?.value ?? 0, unit: 'mg' };
  }
  if (id === 'omega-3' || id === 'omega-6' || id === 'trans-fat') {
    if (id === 'omega-3') {
      const g = data.mealPlanNutritionReportDetail?.omega_3;
      return { total: g != null && Number.isFinite(g) ? g : 0, unit: 'g' };
    }
    if (id === 'omega-6') {
      const g = data.mealPlanNutritionReportDetail?.omega_6;
      return { total: g != null && Number.isFinite(g) ? g : 0, unit: 'g' };
    }
    const k = data.kpis.find((x) => x.label.trim().toLowerCase() === 'trans fat');
    const g = data.fatSubtypeGrams?.trans;
    const fromKpi = k != null && Number.isFinite(k.value) ? k.value : null;
    const fromG = typeof g === 'number' && Number.isFinite(g) ? g : null;
    return { total: fromKpi ?? fromG ?? 0, unit: 'g' };
  }
  if (
    sectionTitle === 'Vitamins'
    || sectionTitle === 'Minerals and Trace Elements'
    || sectionTitle === 'Other Micronutrients'
  ) {
    return null;
  }
  return { total: 0, unit: 'g' };
}

function perMealAmountsForRow(id: string, r: Record<string, unknown>, data: AnalysisData): number {
  if (id === 'energy-row') {
    return readFromMeal(
      r,
      ['kcal', 'calories', 'energy', 'totalCalories', 'total_calories'],
      ['calories', 'totalCalories', 'total_calories', 'energy', 'kcal'],
    );
  }
  if (id === 'carbs') return readFromMeal(r, ['carbohydrate', 'carbs', 'carbohydrates'], ['carbohydrate', 'carbs']);
  if (id === 'protein') return readFromMeal(r, ['protein'], ['protein']);
  if (id === 'fat') return readFromMeal(r, ['totalFat', 'total_fat', 'fat'], ['total_fat', 'totalFat', 'fat']);
  if (id === 'water') return readFromMeal(r, ['water', 'water_liters', 'waterL'], ['water']);
  if (id === 'alcohol') return readFromMeal(r, ['alcohol'], ['alcohol']);
  if (id === 'nsp-fibre') {
    return readFromMeal(r, ['dietary_fibre', 'dietaryFibre', 'dietaryFiber', 'fiber', 'fibre', 'dietary_fiber'], [
      'dietary_fibre',
      'dietaryFibre',
      'fibre',
      'fiber',
    ]);
  }
  if (id === 'sugars') {
    return readFromMeal(r, ['sugar', 'sugars'], ['sugar']);
  }
  if (id === 'free-sugars') {
    return readFromMeal(r, ['free_sugars', 'freeSugars', 'freeSugar'], ['free_sugars', 'freeSugars', 'freeSugar']);
  }
  if (id === 'starch') {
    return readFromMeal(r, ['starch'], ['starch']);
  }
  if (id === 'oligosaccharide') {
    return readFromMeal(r, ['oligosaccharide'], ['oligosaccharide']);
  }
  if (id === 'sat-fat') return readFromMeal(r, ['saturatedFat', 'saturated_fat', 'saturated_fat'], ['saturated_fat', 'saturatedFat']);
  if (id === 'mono-fat') return readFromMeal(r, ['mufa', 'MUFA', 'monounsaturatedFat', 'monounsaturated'], [
    'mufa',
    'monounsaturatedFat',
  ]);
  if (id === 'poly-fat') return readFromMeal(r, ['pufa', 'PUFA', 'polyunsaturatedFat', 'polyunsaturated'], [
    'pufa',
    'polyunsaturatedFat',
  ]);
  if (id === 'cholesterol') return readFromMeal(r, ['cholesterol'], ['cholesterol']);
  if (id === 'omega-3' || id === 'omega-6' || id === 'trans-fat') {
    if (id === 'omega-3') {
      return readFromMeal(r, ['omega_3', 'omega3'], ['omega_3']);
    }
    if (id === 'omega-6') {
      return readFromMeal(r, ['omega_6', 'omega6'], ['omega_6']);
    }
    return readFromMeal(r, ['transFat', 'trans_fat', 'trans'], ['trans_fat', 'transFat']);
  }
  return 0;
}

function findMicro(
  micros: NutritionalAnalysisMicrosView | null | undefined,
  rowLabel: string,
) {
  if (!micros?.byStatus?.all?.length) return null;
  const want = rowLabel.trim().toLowerCase();
  return (
    micros.byStatus.all.find(
      (m) =>
        m.displayName.trim().toLowerCase() === want
        || m.nutrientId.trim().toLowerCase() === want.replace(/\s+/g, '_'),
    ) ?? null
  );
}

function buildTargetLine(data: AnalysisData, ctx: RowCtx, totalUnit: string): string | null {
  const { row, sectionId } = ctx;
  if (row.target && row.target !== 'N/A') {
    if (row.limits && row.limits !== '--') {
      return `Target: ${row.target} · Limits ${row.limits}`;
    }
    return `Target: ${row.target}`;
  }
  if (sectionId === 'macronutrients' && row.id === 'protein' && data.proteinTarget > 0) {
    return `Target: ${formatMetric(data.proteinTarget, 0)}${totalUnit} / day`;
  }
  if (row.id === 'nsp-fibre' && data.fibreTarget > 0) {
    return `Target: ${formatMetric(data.fibreTarget, 0)}${totalUnit} / day (reference)`;
  }
  return null;
}

function sortItems(items: FoodContributionItem[]): FoodContributionItem[] {
  return [...items].sort((a, b) => b.amount - a.amount);
}

/**
 * Distribute a daily total across meal names by kcal share (when per-meal grams are missing).
 */
function allocateByEnergyShares(
  data: AnalysisData,
  total: number,
): FoodContributionItem[] {
  const meals = data.energyByMeal;
  if (!meals.length) return [];
  const sumKcal = meals.reduce((s, m) => s + m.kcal, 0);
  const t = sumKcal > 0 ? sumKcal : 1;
  return meals.map((m) => ({
    name: m.name,
    amount: (total * m.kcal) / t,
    hasContribution: m.kcal > 0,
  }));
}

/**
 * Figma 97:8591 — per-food list for a Nutrient Report row.
 */
export function buildFoodContributionsForReportRow(
  data: AnalysisData,
  ctx: RowCtx,
  micros: NutritionalAnalysisMicrosView | null | undefined,
  apiResponse: unknown,
  viewMode: number,
  selectedDayIndex: number,
): FoodContributionResult | null {
  const { row, sectionTitle } = ctx;
  const isMicroSection =
    sectionTitle === 'Vitamins' || sectionTitle === 'Minerals and Trace Elements' || sectionTitle === 'Other Micronutrients';

  if (isMicroSection) {
    const m = findMicro(micros, row.label);
    if (!m) {
      return { nutrientLabel: row.label, unit: 'mg', items: [], targetLine: null };
    }
    const unit = (m.intakeUnit || 'mg').trim();
    const total = m.intakeValue;
    if (total == null || !Number.isFinite(total)) {
      return {
        nutrientLabel: m.displayName,
        unit,
        items: (m.foodSources ?? []).map((name) => ({ name, amount: 0, hasContribution: false })),
        targetLine:
          m.rdaValue != null
            ? `Target: ${formatMetric(m.rdaValue, 0)}${unit} / day`
            : m.ulValue != null
              ? `UL: ${formatMetric(m.ulValue, 0)}${unit} / day`
              : null,
      };
    }
    const src = m.foodSources && m.foodSources.length ? m.foodSources : [];
    if (!src.length) {
      return {
        nutrientLabel: m.displayName,
        unit,
        items: [],
        targetLine:
          m.rdaValue != null
            ? `Target: ${formatMetric(m.rdaValue, 0)}${unit} / day`
            : null,
      };
    }
    const n = src.length;
    const base = total / n;
    const items: FoodContributionItem[] = src.map((name) => {
      const amount = base;
      return { name, amount, hasContribution: amount > 0 };
    });
    return {
      nutrientLabel: m.displayName,
      unit,
      items: sortItems(items),
      targetLine:
        m.rdaValue != null
          ? `Target: ${formatMetric(m.rdaValue, 0)}${unit} / day`
          : m.ulValue != null
            ? `UL: ${formatMetric(m.ulValue, 0)}${unit} / day`
            : null,
    };
  }

  const totals = totalForRow(data, ctx);
  if (!totals) {
    return { nutrientLabel: row.label, unit: 'g', items: [], targetLine: null };
  }
  const { total, unit: totalUnit } = totals;
  const id = row.id;
  const targetLine = buildTargetLine(data, ctx, totalUnit);
  const meals = getMealsArrayFromNutritionalAnalysis(apiResponse, { viewMode, selectedDayIndex });
  if (!meals || meals.length === 0) {
    if (data.energyByMeal.length && total > 0) {
      return {
        nutrientLabel: row.label,
        unit: totalUnit,
        items: sortItems(allocateByEnergyShares(data, total).map((x) => ({ ...x, hasContribution: x.amount > 0.0001 }))),
        targetLine,
      };
    }
    return { nutrientLabel: row.label, unit: totalUnit, items: [], targetLine };
  }

  const fromMeal: FoodContributionItem[] = [];
  for (const raw of meals) {
    if (!raw || typeof raw !== 'object') continue;
    const r = raw as Record<string, unknown>;
    const name = mealLabel(r);
    const a = perMealAmountsForRow(id, r, data);
    fromMeal.push({ name, amount: a, hasContribution: a > 0.0001 });
  }

  const sumParts = fromMeal.reduce((s, x) => s + x.amount, 0);
  if (sumParts > 0.01 && total > 0) {
    return {
      nutrientLabel: row.label,
      unit: totalUnit,
      items: sortItems(
        fromMeal.map((x) => {
          const scaled = (x.amount / sumParts) * total;
          return { name: x.name, amount: scaled, hasContribution: scaled > 0.0001 };
        }),
      ),
      targetLine,
    };
  }

  if (data.energyByMeal.length && total > 0) {
    return {
      nutrientLabel: row.label,
      unit: totalUnit,
      items: sortItems(allocateByEnergyShares(data, total).map((x) => ({ ...x, hasContribution: x.amount > 0.0001 }))),
      targetLine,
    };
  }

  return {
    nutrientLabel: row.label,
    unit: totalUnit,
    items: sortItems(fromMeal.map((x) => ({ ...x, hasContribution: x.amount > 0.0001 }))),
    targetLine,
  };
}

function asRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === 'object' && !Array.isArray(value) ? (value as Record<string, unknown>) : null;
}

/**
 * Normalizes GET .../nutritional-analysis/foods response into UI rows.
 */
export function parseNutritionalAnalysisFoodsResponse(
  payload: unknown,
  opts?: { viewMode?: number; selectedDayIndex?: number },
): FoodContributionItem[] {
  const collectArrays = (): unknown[] => {
    if (Array.isArray(payload)) return payload;
    const root = asRecord(payload);
    const data = root?.data != null ? asRecord(root.data) : root;
    if (!data) return [];
    const isCumulative = opts?.viewMode === 1;
    if (isCumulative) {
      const cumulative = asRecord(data.cumulative);
      const cumulativeFoods = Array.isArray(cumulative?.foods) ? cumulative.foods : null;
      if (cumulativeFoods?.length) return cumulativeFoods;
      const cumulativeDays = Array.isArray(cumulative?.days) ? cumulative.days : null;
      if (cumulativeDays?.length) return cumulativeDays;
    }
    for (const key of ['foods', 'items', 'contributions', 'rows', 'results']) {
      const v = data[key];
      if (Array.isArray(v) && v.length) return v;
    }
    const selectedDayNumber = (opts?.selectedDayIndex ?? 0) + 1;
    const daily = Array.isArray(data.daily) ? data.daily : null;
    if (daily?.length) {
      const dayNode =
        daily.find((d) => {
          const rec = asRecord(d);
          return safeNumber(rec?.dayNumber, Number.NaN) === selectedDayNumber;
        }) ?? daily[0];
      const dayRec = asRecord(dayNode);
      const dayFoods = Array.isArray(dayRec?.foods) ? dayRec.foods : null;
      if (dayFoods?.length) return dayFoods;
    }
    const cumulative = asRecord(data.cumulative);
    const cumulativeFoods = Array.isArray(cumulative?.foods) ? cumulative.foods : null;
    if (cumulativeFoods?.length) return cumulativeFoods;
    return [];
  };
  const arr = collectArrays();
  const out: FoodContributionItem[] = [];
  for (const raw of arr) {
    const o = asRecord(raw);
    if (!o) continue;
    const isDayRow = String(o.itemType ?? '').toLowerCase() === 'day';
    let dayLabel = '';
    if (isDayRow) {
      const rawDayName = typeof o.dayName === 'string' ? o.dayName.trim() : '';
      const dn = safeNumber(o.dayNumber, Number.NaN);
      if (rawDayName) {
        dayLabel = rawDayName;
      } else if (Number.isFinite(dn)) {
        dayLabel = `Day ${dn}`;
      }
    }
    const name = String(
      dayLabel ||
        (o.foodName ??
          o.name ??
          o.label ??
          o.title ??
          o.ingredient ??
          o.ingredientName ??
          o.description ??
          o.recipeName ??
          ''),
    ).trim();
    if (!name) continue;
    let amount = safeNumber(
      o.amount ?? o.quantity ?? o.value ?? o.grams ?? o.contribution ?? o.intake ?? o.amountGrams,
      Number.NaN,
    );
    if (!Number.isFinite(amount)) amount = 0;
    amount = Math.max(0, amount);
    out.push({ name, amount, hasContribution: amount > 0.0001 });
  }
  /** Show in reverse order relative to GET .../nutritional-analysis/foods payload. */
  return [...out].reverse();
}

/** Query value for GET .../nutritional-analysis/foods?nutrient= */
export function nutrientQueryParamFromRow(row: { id: string; nutrientApiKey?: string }): string | null {
  const key = row.nutrientApiKey?.trim().toLowerCase();
  if (key) return key;
  const MACRO_FALLBACK: Record<string, string> = {
    'energy-row': 'energy',
    carbs: 'carbohydrate',
    protein: 'protein',
    fat: 'total_fat',
    water: 'water',
    alcohol: 'alcohol',
    starch: 'starch',
    oligosaccharide: 'oligosaccharide',
    'nsp-fibre': 'dietary_fibre',
    sugars: 'sugar',
    'free-sugars': 'free_sugars',
    'sat-fat': 'saturated_fat',
    'mono-fat': 'monounsaturated_fat',
    'poly-fat': 'polyunsaturated_fat',
    'omega-3': 'omega_3',
    'omega-6': 'omega_6',
    'trans-fat': 'trans_fat',
    cholesterol: 'cholesterol',
  };
  const f = MACRO_FALLBACK[row.id];
  return f ? f.toLowerCase() : null;
}
